import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import { 
  Modal, 
  Button, 
  Form, 
  Pagination, 
  Input, 
  Table, 
  Tag, 
  message, 
  Space, 
  Card, 
  Row, 
  Col, 
  Typography, 
  Checkbox, 
  Badge,
  Popconfirm,
  Tooltip,
  InputNumber
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined, 
  SearchOutlined 
} from '@ant-design/icons';
import RichTextEditor from '../components/RichTextEditor';
import TextPreview from '../components/TextPreview';
import { extractSummary } from '../utils/textParser';
import './NoticeManagement.css';

const { Title, Text } = Typography;

const NoticeManagement = () => {
  const [notices, setNotices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedNotices, setSelectedNotices] = useState(new Set());
  const [currentNotice, setCurrentNotice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotices, setTotalNotices] = useState(0);
  const [limit] = useState(10);


  // 表单状态
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isActive: true,
    order: 0
  });

  // 获取公告列表
  const fetchNotices = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/notices', {
        params: { page, limit, search: searchTerm },
      });
      setNotices(response.data.notices || []);
      setTotalNotices(response.data.totalNotices || 0);
      setTotalPages(response.data.totalPages || 1);
      setSelectedNotices(new Set());
    } catch (error) {
      console.error('获取公告列表失败:', error);
      showAlert('获取公告列表失败', 'error');
    }
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  // 处理搜索输入变化
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // 处理搜索按钮点击
  const handleSearch = () => {
    setSearchTerm(inputValue);
    setPage(1);
  };



  // 显示提示
  const showAlert = (msg, type = 'info') => {
    if (type === 'success') {
      message.success(msg);
    } else if (type === 'danger' || type === 'error') {
      message.error(msg);
    } else if (type === 'warning') {
      message.warning(msg);
    } else {
      message.info(msg);
    }
  };

  // 打开创建/编辑模态框
  const handleShowModal = (notice = null) => {
    if (notice) {
      setCurrentNotice(notice);
      setFormData({
        title: notice.title,
        content: notice.content,
        isActive: notice.isActive,
        order: notice.order || 0
      });
      setIsEditing(true);
    } else {
      setCurrentNotice(null);
      setFormData({
        title: '',
        content: '',
        isActive: true,
        order: 0
      });
      setIsEditing(false);
    }
    setShowModal(true);
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentNotice(null);
    setFormData({
      title: '',
      content: '',
      isActive: true,
      order: 0
    });
  };



  // 提交表单
  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      showAlert('标题和内容不能为空', 'error');
      return;
    }

    try {
      if (isEditing) {
        await API.put(`/api/admin/notices/${currentNotice._id}`, formData);
        showAlert('公告更新成功', 'success');
      } else {
        await API.post('/api/admin/notices', formData);
        showAlert('公告创建成功', 'success');
      }
      handleCloseModal();
      fetchNotices();
    } catch (error) {
      console.error('操作失败:', error);
      const errorMessage = error.response?.data?.message || '操作失败';
      showAlert(errorMessage, 'error');
    }
  };

  // 删除单个公告
  const handleDeleteNotice = async (noticeId) => {
    try {
      await API.delete(`/api/admin/notices/${noticeId}`);
      showAlert('公告删除成功', 'success');
      fetchNotices();
    } catch (error) {
      console.error('删除公告失败:', error);
      showAlert('删除公告失败', 'error');
    }
  };

  // 批量删除公告
  const handleBulkDelete = async () => {
    const selectedIds = Array.from(selectedNotices);
    if (selectedIds.length === 0) {
      showAlert('请选择要删除的公告', 'warning');
      return;
    }

    try {
      await API.delete('/api/admin/notices', { data: { ids: selectedIds } });
      showAlert(`成功删除 ${selectedIds.length} 条公告`, 'success');
      setSelectedNotices(new Set());
      
      // 检查是否删除了当前页的所有数据
      const remainingNoticesOnCurrentPage = notices.length - selectedIds.length;
      
      // 如果当前页没有剩余数据且不是第一页，跳转到前一页
      if (remainingNoticesOnCurrentPage === 0 && page > 1) {
        setPage(page - 1);
      } else {
        fetchNotices();
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      showAlert('批量删除失败', 'error');
    }
  };

  // 切换公告状态
  const toggleNoticeStatus = async (notice) => {
    try {
      await API.put(`/api/admin/notices/${notice._id}`, {
        ...notice,
        isActive: !notice.isActive
      });
      showAlert(`公告已${notice.isActive ? '禁用' : '启用'}`, 'success');
      fetchNotices();
    } catch (error) {
      console.error('切换状态失败:', error);
      showAlert('切换状态失败', 'error');
    }
  };





  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN');
  };



  return (
    <div className="notice-management">
      {/* 页面头部 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>公告管理</Title>
      </div>

      {/* 统计和搜索区域 */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Text type="secondary">
              总公告数量：<Text strong style={{ color: '#1890ff' }}>{totalNotices}</Text>
            </Text>
          </Col>
          <Col>
            <Input.Group compact>
              <Input
                style={{ width: 300 }}
                placeholder="搜索标题或内容..."
                value={inputValue}
                onChange={handleInputChange}
                onPressEnter={handleSearch}
                suffix={<SearchOutlined onClick={handleSearch} style={{ cursor: 'pointer' }} />}
              />
            </Input.Group>
          </Col>
        </Row>

        <Row justify="space-between" align="middle">
          <Col>
            <Button 
              danger
              icon={<DeleteOutlined />}
              onClick={handleBulkDelete}
            >
              批量删除
            </Button>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => handleShowModal()}
            >
              创建公告
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 公告列表 */}
      <Card>
        <Table
          dataSource={notices}
          rowKey="_id"
          pagination={false}
          scroll={{ x: 1000 }}
          rowSelection={{
            selectedRowKeys: Array.from(selectedNotices),
            onChange: (selectedRowKeys) => {
              setSelectedNotices(new Set(selectedRowKeys));
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              const newSelected = new Set(selectedNotices);
              if (selected) {
                notices.forEach(notice => newSelected.add(notice._id));
              } else {
                notices.forEach(notice => newSelected.delete(notice._id));
              }
              setSelectedNotices(newSelected);
            }
          }}
          columns={[
            {
              title: '标题',
              dataIndex: 'title',
              key: 'title',
              width: 200,
              ellipsis: true,
              render: (text) => (
                <Tooltip title={text}>
                  <Text>{text}</Text>
                </Tooltip>
              )
            },
            {
              title: '内容',
              dataIndex: 'content',
              key: 'content',
              width: 300,
              render: (text) => {
                const summary = extractSummary(text, 100);
                return (
                  <Tooltip 
                    title={
                      <div style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
                        <TextPreview 
                          content={text} 
                          title="" 
                          showTypeTag={true}
                          maxHeight={250}
                          style={{ border: 'none' }}
                        />
                      </div>
                    }
                    overlayStyle={{ maxWidth: 450 }}
                  >
                    <Text ellipsis style={{ maxWidth: 280, display: 'block' }}>
                      {summary}
                    </Text>
                  </Tooltip>
                );
              }
            },
            {
              title: '状态',
              dataIndex: 'isActive',
              key: 'isActive',
              width: 100,
              align: 'center',
              render: (isActive) => (
                <Badge 
                  status={isActive ? 'success' : 'error'} 
                  text={isActive ? '启用' : '禁用'}
                />
              )
            },
            {
              title: '排序',
              dataIndex: 'order',
              key: 'order',
              width: 80,
              align: 'center',
              render: (order) => (
                <Tag color="blue">{order || 0}</Tag>
              )
            },
            {
              title: '创建时间',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 150,
              align: 'center',
              render: (date) => (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatDate(date)}
                </Text>
              )
            },
            {
              title: '操作',
              key: 'action',
              width: 200,
              align: 'center',
              render: (_, record) => (
                <Space>
                  <Tooltip title={record.isActive ? '禁用' : '启用'}>
                    <Button
                      type={record.isActive ? 'default' : 'primary'}
                      size="small"
                      icon={record.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => toggleNoticeStatus(record)}
                    />
                  </Tooltip>
                  <Tooltip title="编辑">
                    <Button
                      type="default"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleShowModal(record)}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="确定要删除这个公告吗？"
                    description="此操作不可撤销，请谨慎操作！"
                    onConfirm={() => handleDeleteNotice(record._id)}
                  >
                    <Tooltip title="删除">
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              )
            }
          ]}
        />
      </Card>

      {/* 分页 */}
      <Row justify="space-between" align="middle" style={{ marginTop: 24 }}>
        <Col>
          <Pagination
            current={page}
            total={totalNotices}
            pageSize={limit}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
            }
            onChange={(page) => setPage(page)}
          />
        </Col>
        <Col>
          <Text type="secondary">
            当前第 {page} 页，共 {totalPages} 页
          </Text>
        </Col>
      </Row>

      {/* 创建/编辑公告模态框 */}
      <Modal
        title={isEditing ? '编辑公告' : '创建公告'}
        open={showModal}
        onCancel={handleCloseModal}
        width={900}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {isEditing ? '更新' : '创建'}
          </Button>
        ]}
      >
        <Form layout="vertical">
          <Form.Item label="标题">
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="请输入公告标题"
            />
          </Form.Item>

          <Form.Item label="内容">
            <RichTextEditor
              value={formData.content}
              onChange={(content) => setFormData(prev => ({ ...prev, content }))}
              placeholder="请输入公告内容，支持Markdown、HTML、纯文本等多种格式"
              height={350}
              showPreview={true}
              showToolbar={true}
              maxLength={20000}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="排序序号" help="数字越大排序越靠前">
                <InputNumber
                  style={{ width: '100%' }}
                  name="order"
                  value={formData.order}
                  onChange={(value) => setFormData(prev => ({ ...prev, order: value || 0 }))}
                  placeholder="0"
                  min={0}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="设置">
                <div style={{ paddingTop: 8 }}>
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  >
                    启用状态
                  </Checkbox>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

    </div>
  );
};

export default NoticeManagement; 